import { z } from 'zod';
// Auth validation schemas
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    tenantId: z.string().uuid().optional(),
});
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password is too long'),
    name: z.string().min(2, 'Name must be at least 2 characters'),
});
/**
 * Body for an invited user creating their account directly from an invite link.
 * The email is taken from the (already-verified) invite token, never from the
 * request body — so an invitee can't bind the new account to a different email.
 */
export const acceptInvitationRegisterSchema = z.object({
    name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100),
    password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password is too long'),
});
/** Super-admin grant of a complimentary (free) license to a tenant. */
export const grantComplimentarySchema = z.object({
    planTier: z.number().int().min(0).max(5).optional(),
    seatCount: z.number().int().min(1, 'At least one seat').max(1000),
    reason: z.string().trim().max(500).optional(),
    expiresAt: z.string().datetime().optional(),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
});
// BOQ validation schemas
export const billItemCreateSchema = z.object({
    jobId: z.string().uuid(),
    catalogueItemId: z.string().uuid().optional(),
    costCentreId: z.string().uuid(),
    bLoad: z.number().int().positive().optional().default(1),
    quantity: z.number().optional().default(0),
    quantityFormula: z.string().max(500).optional(),
    unitPrice: z.number().optional().default(0),
    workup: z.string().optional(),
    supplierId: z.string().uuid().optional(),
});
export const billItemUpdateSchema = z.object({
    quantity: z.number().optional(),
    quantityFormula: z.string().max(500).optional(),
    unitPrice: z.number().optional(),
    workup: z.string().optional(),
    supplierId: z.string().uuid().nullable().optional(),
    costCentreId: z.string().uuid().optional(),
    bLoad: z.number().int().positive().optional(),
});
export const repriceOptionsSchema = z.object({
    costCentre: z.string().optional(),
    bLoad: z.number().int().positive().optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    scope: z.enum(['all', 'costCentre', 'load', 'selected']).optional(),
});
export const recipeExplosionSchema = z.object({
    quantity: z.number().positive(),
    variableValues: z.record(z.string(), z.number()).optional().default({}),
    /** When set, explode the assembly override stored on this bill line instead of the saved catalogue assembly. */
    billItemId: z.string().uuid().optional(),
    options: z.object({
        removeParent: z.boolean().optional().default(false),
        explodeRecipes: z.boolean().optional().default(true),
        maxDepth: z.number().int().min(1).max(10).optional(),
    }).optional(),
});
// Assembly (recipe) override on a BOQ line — editable copy of the assembly's items
// stored in bill_items.metadata.assemblyOverride. "Assembly" / "Assembly Items" is the
// user-facing name for the catalogue recipe / recipe_components layer underneath.
export const assemblyItemSchema = z.object({
    /** Catalogue price code, or null for an ad-hoc (free-text) item. */
    priceCode: z.string().max(50).nullable().optional(),
    description: z.string().min(1).max(500),
    quantity: z.number().finite(),
    unitPrice: z.number().finite().min(0),
    unitCode: z.string().max(50).nullable().optional(),
    unitDisplay: z.string().max(50).nullable().optional(),
    costCentreCode: z.string().max(50).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    adhoc: z.boolean().optional().default(false),
});
export const assemblyItemsSaveSchema = z.object({
    /** Line-local display name for the assembly; null/omitted ⇒ keep the catalogue description. */
    name: z.string().max(500).nullable().optional(),
    items: z.array(assemblyItemSchema).max(200),
});
export const saveLineAsAssemblySchema = z.object({
    name: z.string().min(1).max(500),
});
// Write a line's edited items back to its EXISTING catalogue assembly (name optional — keep current).
export const updateAssemblySchema = z.object({
    name: z.string().max(500).nullable().optional(),
});
// Catalogue validation schemas
export const catalogueItemCreateSchema = z.object({
    priceCode: z.string().min(1).max(50),
    description: z.string().min(1).max(500),
    costCentreId: z.string().uuid().optional(),
    unitId: z.string().uuid().optional(),
    isRecipe: z.boolean().optional().default(false),
    isRecipeIngredient: z.boolean().optional().default(false),
    template: z.string().optional(),
    specification: z.string().optional(),
});
export const catalogueItemUpdateSchema = z.object({
    description: z.string().min(1).max(500).optional(),
    costCentreId: z.string().uuid().nullable().optional(),
    unitId: z.string().uuid().nullable().optional(),
    isRecipe: z.boolean().optional(),
    isRecipeIngredient: z.boolean().optional(),
    template: z.string().optional(),
    specification: z.string().optional(),
    isArchived: z.boolean().optional(),
});
export const priceCreateSchema = z.object({
    price: z.number(),
    effectiveDate: z.string().or(z.date()).optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    isManualOverride: z.boolean().optional().default(false),
    changeReason: z.string().max(500).optional(),
});
// Job validation schemas
export const jobCreateSchema = z.object({
    jobNo: z.string().min(1).max(20),
    name: z.string().max(200).optional(),
    status: z.string().max(50).optional().default('active'),
    clientId: z.string().uuid().optional(),
    estimatorId: z.string().uuid().optional(),
    startDate: z.string().or(z.date()).optional(),
    endDate: z.string().or(z.date()).optional(),
    contractPrice: z.number().optional().default(0),
    contractGst: z.number().optional().default(0),
    budget: z.number().optional().default(0),
    siteAddress: z.record(z.string(), z.unknown()).optional(),
});
export const jobUpdateSchema = z.object({
    name: z.string().max(200).optional(),
    status: z.string().max(50).optional(),
    clientId: z.string().uuid().nullable().optional(),
    estimatorId: z.string().uuid().nullable().optional(),
    startDate: z.string().or(z.date()).nullable().optional(),
    endDate: z.string().or(z.date()).nullable().optional(),
    contractPrice: z.number().optional(),
    contractGst: z.number().optional(),
    budget: z.number().optional(),
    isArchived: z.boolean().optional(),
    siteAddress: z.record(z.string(), z.unknown()).optional(),
});
// Order validation schemas
export const orderCreateSchema = z.object({
    orderNumber: z.string().min(1).max(100),
    jobId: z.string().uuid(),
    costCentreId: z.string().uuid(),
    bLoad: z.number().int().positive().optional().default(1),
    supplierId: z.string().uuid().optional(),
    orderDate: z.string().or(z.date()).optional(),
    deliveryDate: z.string().or(z.date()).optional(),
    notes: z.string().optional(),
});
export const orderUpdateSchema = z.object({
    supplierId: z.string().uuid().nullable().optional(),
    orderDate: z.string().or(z.date()).nullable().optional(),
    deliveryDate: z.string().or(z.date()).nullable().optional(),
    status: z.string().max(50).optional(),
    isOrdered: z.boolean().optional(),
    notes: z.string().optional(),
});
// Supplier validation schemas
export const supplierCreateSchema = z.object({
    code: z.string().min(1).max(20),
    name: z.string().min(1).max(200),
    groupId: z.string().uuid().optional(),
    contactName: z.string().max(100).optional(),
    phone: z.string().max(50).optional(),
    mobile: z.string().max(50).optional(),
    email: z.string().email().optional(),
    address: z.record(z.string(), z.unknown()).optional(),
    abn: z.string().max(20).optional(),
    isGstRegistered: z.boolean().optional().default(true),
    paymentTerms: z.number().int().optional().default(30),
    area: z.string().max(50).optional(),
    notes: z.string().optional(),
});
export const supplierUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    groupId: z.string().uuid().nullable().optional(),
    contactName: z.string().max(100).optional(),
    phone: z.string().max(50).optional(),
    mobile: z.string().max(50).optional(),
    email: z.string().email().nullable().optional(),
    address: z.record(z.string(), z.unknown()).optional(),
    abn: z.string().max(20).optional(),
    isGstRegistered: z.boolean().optional(),
    paymentTerms: z.number().int().optional(),
    area: z.string().max(50).optional(),
    notes: z.string().optional(),
    isArchived: z.boolean().optional(),
});
export const supplierPriceCreateSchema = z.object({
    priceCode: z.string().min(1).max(50),
    supplierReference: z.string().max(100).optional(),
    price: z.number(),
    effectiveDate: z.string().or(z.date()).optional(),
    expiryDate: z.string().or(z.date()).optional(),
    priceLevel: z.number().int().min(1).max(5).optional().default(1),
    area: z.string().max(50).optional(),
    comments: z.string().max(500).optional(),
});
// Quote / Proposal validation schemas
export const createQuoteSchema = z.object({
    jobId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    description: z.string().max(500).optional(),
    siteAddress: z.string().max(500).optional(),
    siteSuburb: z.string().max(100).optional(),
    siteState: z.string().max(50).optional(),
    sitePostcode: z.string().max(20).optional(),
    validUntil: z.string().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    coverLetter: z.string().optional(),
    termsConditions: z.string().optional(),
    taxRate: z.number().min(0).max(100).optional(),
    depositType: z.enum(['percentage', 'fixed', 'none']).optional(),
    depositValue: z.number().min(0).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(200).optional(),
    fromTemplateId: z.string().uuid().optional(),
});
export const updateQuoteSchema = z.object({
    description: z.string().max(500).optional(),
    contactId: z.string().uuid().nullable().optional(),
    jobId: z.string().uuid().nullable().optional(),
    siteAddress: z.string().max(500).optional(),
    siteSuburb: z.string().max(100).optional(),
    siteState: z.string().max(50).optional(),
    sitePostcode: z.string().max(20).optional(),
    validUntil: z.string().nullable().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    coverLetter: z.string().optional(),
    termsConditions: z.string().optional(),
    taxRate: z.number().min(0).max(100).optional(),
    depositType: z.enum(['percentage', 'fixed', 'none']).optional(),
    depositValue: z.number().min(0).optional(),
    isTemplate: z.boolean().optional(),
    templateName: z.string().max(200).optional(),
});
export const createQuoteItemSchema = z.object({
    lineType: z.enum(['product', 'heading', 'subtotal', 'comment', 'assembly', 'assembly_item', 'running_subtotal']).optional().default('product'),
    parentItemId: z.string().uuid().optional(),
    catalogueItemId: z.string().uuid().optional(),
    priceCode: z.string().max(50).optional(),
    description: z.string().min(1).max(2000),
    quantity: z.number().optional().default(1),
    unitId: z.string().uuid().optional(),
    unitDisplay: z.string().max(50).optional(),
    unitCost: z.number().optional().default(0),
    markupPercent: z.number().optional().default(0),
    unitPrice: z.number().optional().default(0),
    costCentreId: z.string().uuid().optional(),
    assemblyId: z.string().max(100).optional(),
    isVisible: z.boolean().optional().default(true),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
});
export const updateQuoteItemSchema = z.object({
    lineType: z.enum(['product', 'heading', 'subtotal', 'comment', 'assembly', 'assembly_item', 'running_subtotal']).optional(),
    parentItemId: z.string().uuid().nullable().optional(),
    catalogueItemId: z.string().uuid().nullable().optional(),
    priceCode: z.string().max(50).optional(),
    description: z.string().min(1).max(2000).optional(),
    quantity: z.number().optional(),
    unitId: z.string().uuid().nullable().optional(),
    unitDisplay: z.string().max(50).optional(),
    unitCost: z.number().optional(),
    markupPercent: z.number().optional(),
    unitPrice: z.number().optional(),
    costCentreId: z.string().uuid().nullable().optional(),
    assemblyId: z.string().max(100).nullable().optional(),
    isVisible: z.boolean().optional(),
    notes: z.string().optional(),
    internalNotes: z.string().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const reorderQuoteItemsSchema = z.object({
    items: z.array(z.object({
        id: z.string().uuid(),
        sortOrder: z.number().int().min(0),
        parentItemId: z.string().uuid().nullable().optional(),
    })),
});
// Variation Order schemas
export const createVariationOrderSchema = z.object({
    jobId: z.string().uuid(),
    description: z.string().max(500).optional(),
    additionalText: z.string().optional(),
    mode: z.enum(['simple', 'detailed']).optional().default('simple'),
    markupPercent: z.number().min(0).max(100).optional().default(0),
});
export const updateVariationOrderSchema = z.object({
    description: z.string().max(500).optional(),
    additionalText: z.string().optional(),
    mode: z.enum(['simple', 'detailed']).optional(),
    markupPercent: z.number().min(0).max(100).optional(),
});
export const createVariationLineSchema = z.object({
    description: z.string().min(1).max(500),
    lineType: z.enum(['debit', 'credit']).optional().default('debit'),
    costCentreId: z.string().uuid().optional(),
    catalogueItemId: z.string().uuid().optional(),
    unitId: z.string().uuid().optional(),
    unitDisplay: z.string().max(50).optional(),
    quantity: z.number().optional().default(1),
    unitPrice: z.number().optional().default(0),
    amountExGst: z.number().optional().default(0),
    notes: z.string().optional(),
});
export const updateVariationLineSchema = z.object({
    description: z.string().min(1).max(500).optional(),
    lineType: z.enum(['debit', 'credit']).optional(),
    costCentreId: z.string().uuid().nullable().optional(),
    catalogueItemId: z.string().uuid().nullable().optional(),
    unitId: z.string().uuid().nullable().optional(),
    unitDisplay: z.string().max(50).optional(),
    quantity: z.number().optional(),
    unitPrice: z.number().optional(),
    amountExGst: z.number().optional(),
    notes: z.string().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
// ============================================================
// ALLOWANCES (Provisional Sums / Prime Cost)
// ============================================================
export const allowanceTypeSchema = z.enum(['PS', 'PC']);
/** Margin must be 0 ≤ x < 100 (100 would mean infinite client price under true margin). */
export const allowanceMarginSchema = z.number().min(0).lt(100);
export const markAllowanceSchema = z.object({
    type: allowanceTypeSchema,
    notes: z.string().max(2000).optional(),
});
export const updateAllowanceSchema = z.object({
    type: allowanceTypeSchema.optional(),
    notes: z.string().max(2000).optional(),
    /** Override the job-level default margin for this line. Pass null to clear. */
    marginPercent: allowanceMarginSchema.nullable().optional(),
});
/** PATCH /api/allowances/jobs/:jobId/default-margin */
export const setJobAllowanceMarginSchema = z.object({
    marginPercent: allowanceMarginSchema,
});
/** POST /api/allowances/jobs/:jobId/adhoc — create a new ad-hoc allowance line */
export const createAdhocAllowanceSchema = z.object({
    type: allowanceTypeSchema,
    description: z.string().min(1).max(500),
    amountExGst: z.number().min(0),
    costCentreId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    marginPercent: allowanceMarginSchema.optional(),
});
/** POST /api/allowances/bill-items/:childId/allocate — link a child bill item to an allowance parent */
export const allocateBillItemSchema = z.object({
    /** UUID of the allowance bill_item this child belongs to. Pass null to clear. */
    allowanceBillItemId: z.string().uuid().nullable(),
});
export const recordAllowanceReconciliationSchema = z.object({
    actualCostExGst: z.number(),
    sourceType: z.enum(['manual', 'order', 'invoice', 'variation']).optional().default('manual'),
    sourceId: z.string().uuid().optional(),
    notes: z.string().max(2000).optional(),
    recordedAt: z.string().or(z.date()).optional(),
});
export const updateAllowanceReconciliationSchema = z.object({
    actualCostExGst: z.number().optional(),
    sourceType: z.enum(['manual', 'order', 'invoice', 'variation']).optional(),
    sourceId: z.string().uuid().nullable().optional(),
    notes: z.string().max(2000).optional(),
    recordedAt: z.string().or(z.date()).optional(),
});
export const convertAllowanceSchema = z.object({
    finalAmountExGst: z.number(),
    createVariation: z.boolean().optional().default(false),
    variationDescription: z.string().max(500).optional(),
});
// ============================================================
// TIMESHEETS — Phase 1
// ============================================================
const uuidOptional = z.string().uuid().nullable().optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const positiveHours = z.number().positive().max(24);
export const createEmployeeSchema = z.object({
    code: z.string().max(50).optional(),
    workerType: z.enum(['employee', 'subcontractor']).default('employee'),
    firstName: z.string().max(100).optional(),
    lastName: z.string().max(100).optional(),
    email: z.string().email().max(255).optional().or(z.literal('')),
    phone: z.string().max(50).optional(),
    supplierId: uuidOptional,
    userId: uuidOptional,
    defaultCostRate: z.number().min(0).optional(),
    defaultChargeRate: z.number().min(0).optional(),
    /** Plain PIN — server hashes. 4-6 digits. */
    pin: z.string().regex(/^\d{4,6}$/).optional(),
}).refine(d => d.firstName || d.lastName || d.code, {
    message: 'At least one of firstName, lastName, or code is required',
});
export const updateEmployeeSchema = z.object({
    code: z.string().max(50).nullable().optional(),
    workerType: z.enum(['employee', 'subcontractor']).optional(),
    firstName: z.string().max(100).nullable().optional(),
    lastName: z.string().max(100).nullable().optional(),
    email: z.string().email().max(255).nullable().optional().or(z.literal('')),
    phone: z.string().max(50).nullable().optional(),
    supplierId: uuidOptional,
    userId: uuidOptional,
    defaultCostRate: z.number().min(0).optional(),
    defaultChargeRate: z.number().min(0).optional(),
    /** null clears the PIN. */
    pin: z.string().regex(/^\d{4,6}$/).nullable().optional(),
    isArchived: z.boolean().optional(),
});
export const createTimesheetEntrySchema = z.object({
    employeeId: z.string().uuid(),
    jobId: z.string().uuid(),
    costCentreId: uuidOptional,
    workDate: isoDate,
    hours: positiveHours,
    rateType: z.enum(['base', 'ot15', 'ot2']).default('base'),
    costRate: z.number().min(0).nullable().optional(),
    chargeRate: z.number().min(0).nullable().optional(),
    notes: z.string().max(1000).optional(),
    allocatedToAllowance: uuidOptional,
    clockInAt: z.string().datetime().nullable().optional(),
    clockOutAt: z.string().datetime().nullable().optional(),
    clockInLat: z.number().min(-90).max(90).nullable().optional(),
    clockInLng: z.number().min(-180).max(180).nullable().optional(),
    clockInAccuracyM: z.number().min(0).nullable().optional(),
});
export const updateTimesheetEntrySchema = z.object({
    costCentreId: uuidOptional,
    workDate: isoDate.optional(),
    hours: positiveHours.optional(),
    rateType: z.enum(['base', 'ot15', 'ot2']).optional(),
    costRate: z.number().min(0).nullable().optional(),
    chargeRate: z.number().min(0).nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
    allocatedToAllowance: uuidOptional,
    clockInAt: z.string().datetime().nullable().optional(),
    clockOutAt: z.string().datetime().nullable().optional(),
});
export const submitTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
});
export const approveTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
});
export const rejectTimesheetEntriesSchema = z.object({
    entryIds: z.array(z.string().uuid()).min(1).max(500),
    reason: z.string().max(1000).optional(),
});
// ============================================================================
// Timesheet Me — mobile self-entry (PIN auth)
// ============================================================================
/** Login: tenant slug from URL param, employee code + PIN from body. */
export const timesheetMeLoginSchema = z.object({
    employeeCode: z.string().trim().min(1, 'Employee code is required').max(50),
    pin: z.string().regex(/^\d{4,6}$/, 'PIN must be 4–6 digits'),
});
/** Self-create — employee never sets their own employeeId, never sets rates. */
export const timesheetMeCreateEntrySchema = z.object({
    jobId: z.string().uuid(),
    costCentreId: uuidOptional,
    workDate: isoDate,
    hours: positiveHours,
    rateType: z.enum(['base', 'ot15', 'ot2']).default('base'),
    notes: z.string().max(1000).optional(),
});
/** Self-update — only own drafts; same field set as create. */
export const timesheetMeUpdateEntrySchema = z.object({
    jobId: z.string().uuid().optional(),
    costCentreId: uuidOptional,
    workDate: isoDate.optional(),
    hours: positiveHours.optional(),
    rateType: z.enum(['base', 'ot15', 'ot2']).optional(),
    notes: z.string().max(1000).nullable().optional(),
});
// ============================================================================
// Timesheet Settings (admin-configurable, stored in company_settings.metadata)
// ============================================================================
export const updateTimesheetSettingsSchema = z.object({
    timesheetSelfEntryLockoutDays: z.number().int().min(1).max(365),
});
// ============================================================================
// Client Portal Content (builder-curated, stored in company_settings.metadata)
// ============================================================================
// Only http(s) URLs — blocks javascript:/data: schemes from reaching an href.
const httpUrl = z
    .string()
    .trim()
    .max(500)
    .url()
    .refine((u) => /^https?:\/\//i.test(u), { message: 'Link must start with http:// or https://' });
export const portalContentSchema = z.object({
    portalContacts: z
        .array(z.object({
        name: z.string().trim().min(1).max(120),
        role: z.string().trim().max(120).optional(),
        email: z.string().trim().max(200).email().optional().or(z.literal('')),
        phone: z.string().trim().max(40).optional(),
    }))
        .max(20)
        .optional(),
    portalUsefulLinks: z
        .array(z.object({
        label: z.string().trim().min(1).max(120),
        url: httpUrl,
        description: z.string().trim().max(300).optional(),
    }))
        .max(50)
        .optional(),
    portalFaqs: z
        .array(z.object({
        question: z.string().trim().min(1).max(300),
        answer: z.string().trim().min(1).max(3000),
    }))
        .max(100)
        .optional(),
    // Free-form rich FAQ page (HTML). Sanitized client-side before save AND on
    // render; generous cap for an authored page with formatting/images.
    portalFaqHtml: z.string().max(50000).optional(),
});
// ============================================================================
// Prestart Selections (a selection = a PC/PS allowance the client resolves)
// ============================================================================
export const selectionEnableSchema = z.object({
    required: z.boolean().optional(),
    // ISO date string (YYYY-MM-DD or full ISO); null clears the deadline.
    deadline: z.string().trim().max(40).optional().nullable(),
    // 'flat' = each option is a flat total (default); 'qty_rate' = option priced per-unit × the
    // line's measured BOQ quantity (e.g. bricks).
    pricingMode: z.enum(['flat', 'qty_rate']).optional(),
});
// A per-unit/flat add-on cost on an option (e.g. extra laying/cleaning labour).
export const selectionAddOnSchema = z.object({
    label: z.string().trim().min(1).max(120),
    rate: z.number().min(0).max(99999999).optional(), // per-unit, × line quantity
    flat: z.number().min(0).max(99999999).optional(), // one-off
});
export const selectionOptionSchema = z.object({
    name: z.string().trim().min(1).max(500),
    // Rich HTML (sanitised on render) — markup needs more room than plain text.
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    priceExGst: z.number().min(0).max(99999999), // flat-mode total
    unitRate: z.number().min(0).max(99999999).optional(), // qty_rate-mode per-unit cost
    imageUrl: httpUrl.optional().or(z.literal('')),
    catalogueItemId: z.string().uuid().optional().nullable(),
    isDefault: z.boolean().optional(),
    isStandardInclusion: z.boolean().optional(),
    addOns: z.array(selectionAddOnSchema).max(10).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const selectionOptionUpdateSchema = selectionOptionSchema.partial();
// Homeowner picking an option (portal write).
export const selectionChoiceSchema = z.object({
    optionId: z.string().uuid(),
    note: z.string().trim().max(1000).optional().or(z.literal('')),
});
// Staff setting/clearing the client's choice on their behalf (builder write).
// optionId null = clear the current selection.
export const selectionStaffChoiceSchema = z.object({
    optionId: z.string().uuid().nullable(),
});
// ── Selection TEMPLATES (reusable library) + SECTIONS (headings) ─────────────
// Client-generated ids for in-document nodes (uuid or nanoid).
const nodeId = z.string().trim().min(1).max(64);
// A template/section ITEM is either a priced client Selection (with options) or a
// standard Inclusion (heading/text line, no choice).
export const templateItemSchema = z.object({
    id: nodeId,
    type: z.enum(['selection', 'inclusion']),
    name: z.string().trim().min(1).max(500),
    // Rich HTML (sanitised on render): the inclusion text, or selection notes.
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    // selection-only:
    pricingMode: z.enum(['flat', 'qty_rate']).optional(),
    required: z.boolean().optional(),
    defaultQuantity: z.number().min(0).max(99999999).optional(),
    estimateExGst: z.number().min(0).max(99999999).optional(), // the allowance estimate (line unit_price)
    costCentreCode: z.string().trim().max(20).optional().or(z.literal('')),
    options: z.array(selectionOptionSchema).max(80).optional(),
});
export const templateSectionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(200),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    items: z.array(templateItemSchema).max(300),
});
export const selectionTemplateSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional().or(z.literal('')),
    sections: z.array(templateSectionSchema).max(80),
});
// Duplicate a template (optional new name).
export const templateDuplicateSchema = z.object({
    name: z.string().trim().min(1).max(200).optional(),
});
// Apply a template's structure onto a job.
export const applyTemplateSchema = z.object({
    jobId: z.string().uuid(),
});
// Job-side spec document = ordered sections, each with a heading + inclusion rows.
// (Priced selections are bill_items tagged with metadata.selection.sectionId.)
export const specInclusionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(500),
    description: z.string().trim().max(8000).optional().or(z.literal('')),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
export const specSectionSchema = z.object({
    id: nodeId,
    name: z.string().trim().min(1).max(200),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    inclusions: z.array(specInclusionSchema).max(300).optional(),
});
export const jobSpecSchema = z.object({
    sections: z.array(specSectionSchema).max(80),
});
// Assign a selection (bill_item) to a section (null = ungrouped).
export const selectionSectionSchema = z.object({
    sectionId: nodeId.nullable(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
});
// ============================================================================
// Platform Admin — Usage Insights
// ============================================================================
export const adminTenantListQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
});
export const adminTenantIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// ============================================================================
// Platform Admin — Customer lead cleanup & salespeople management
// ============================================================================
// Bulk soft-delete a set of leads (is_deleted = true; recoverable).
export const bulkDeleteLeadsSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(200),
});
// Clean up leads by status (default: unverified sign-ups). Soft-deletes matching
// leads; `onlyUnprovisioned` protects any lead already attached to a real tenant,
// and `preview` returns the count that WOULD be deleted without deleting.
export const cleanupLeadsSchema = z.object({
    status: z.string().trim().min(1).max(50).default('pending_verification'),
    olderThanDays: z.number().int().min(0).max(3650).optional(),
    onlyUnprovisioned: z.boolean().default(true),
    preview: z.boolean().default(false),
});
// Toggle whether a platform user appears in the lead-assignment salesperson list.
export const setSalespersonSchema = z.object({
    isSalesperson: z.boolean(),
});
export const adminUserIdParamsSchema = z.object({
    userId: z.string().uuid(),
});
// Free/consumer email domains — a corporate lead usually has its own domain, so a
// free-mail address is a (soft) signal to eyeball a lead's genuineness. NOT a
// block: plenty of real sole-trader builders use gmail/bigpond.
export const FREE_EMAIL_DOMAINS = [
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.com.au', 'ymail.com',
    'hotmail.com', 'hotmail.com.au', 'outlook.com', 'outlook.com.au', 'live.com',
    'live.com.au', 'msn.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
    'protonmail.com', 'proton.me', 'gmx.com', 'mail.com', 'yandex.com',
    'bigpond.com', 'bigpond.net.au', 'optusnet.com.au', 'iinet.net.au',
    'tpg.com.au', 'internode.on.net', 'westnet.com.au',
];
export function isFreeEmailDomain(domain) {
    if (!domain)
        return false;
    return FREE_EMAIL_DOMAINS.includes(domain.trim().toLowerCase());
}
// ============================================================================
// Platform Admin — API Keys (MCP / programmatic access)
// ============================================================================
export const adminCreateApiKeySchema = z.object({
    tenantId: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
    isAdmin: z.boolean().default(false),
});
export const adminApiKeyIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// Self-service API keys (public API governance — /api/v1/keys). The tenant comes
// from the signed-in user (never the body) and is_admin is always false, so this
// schema only carries the name + global scope. Per-domain scopes layer on later.
export const createApiKeySchema = z.object({
    name: z.string().trim().min(1).max(100),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
});
export const apiKeyIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// Per-domain API-key scopes (Phase C.2). Domains map 1:1 to the v1 resource
// routers. A key with NO scopes set has full access at its global read/write
// level (backwards-compatible); once any scope is set, it's an ALLOWLIST.
export const API_KEY_DOMAINS = ['jobs', 'catalogue', 'suppliers', 'cost-centres', 'contacts', 'units', 'orders', 'boq', 'selections', 'usage'];
export const apiKeyScopeActionSchema = z.enum(['none', 'read', 'read_write']);
export const setApiKeyScopesSchema = z.object({
    scopes: z.record(z.enum(API_KEY_DOMAINS), apiKeyScopeActionSchema),
});
// Outbound webhooks (Phase E). MVP "safe to expose" events — financial internals
// (invoice.paid, approval.*, payment_batch.*, transaction.*, claim.*) are never
// exposed.
export const WEBHOOK_EVENTS = [
    'job.created',
    'job.updated',
    'order.created',
    'order.status_changed',
    'catalogue_item.created',
    'catalogue_item.price_updated',
    'rfq.created',
    'rfq.response_received',
];
export const createWebhookSchema = z.object({
    url: z.string().url().max(2048),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1),
    description: z.string().trim().max(200).optional(),
});
export const updateWebhookSchema = z.object({
    url: z.string().url().max(2048).optional(),
    events: z.array(z.enum(WEBHOOK_EVENTS)).min(1).optional(),
    description: z.string().trim().max(200).optional(),
    active: z.boolean().optional(),
});
export const webhookIdParamsSchema = z.object({
    id: z.string().uuid(),
});
// ============================================================================
// MCP OAuth (Tier 2B) — claude.ai / connector authorization flow
// ============================================================================
/** RFC 7591 dynamic client registration. Unknown metadata is accepted and
 *  ignored (passthrough) per spec. */
export const oauthRegisterClientSchema = z.object({
    redirect_uris: z.array(z.string().url().max(500)).min(1).max(10),
    client_name: z.string().trim().max(200).optional(),
}).passthrough();
/** Consent approval posted by the SPA consent page (JWT-authenticated). */
export const oauthApproveSchema = z.object({
    client_id: z.string().min(1).max(100),
    redirect_uri: z.string().url().max(500),
    state: z.string().max(1000).optional(),
    code_challenge: z.string().min(43).max(128),
    scope: z.enum(['read_only', 'read_write']).default('read_only'),
});
/** RFC 6749 token exchange (authorization_code + PKCE, public client). */
export const oauthTokenSchema = z.object({
    grant_type: z.literal('authorization_code'),
    code: z.string().min(1).max(200),
    redirect_uri: z.string().url().max(500),
    client_id: z.string().min(1).max(100),
    code_verifier: z.string().min(43).max(128),
});
// ============================================================================
// In-app assistant ("Ask Costed")
// ============================================================================
const assistantMessageSchema = z.object({
    // user/assistant for chat turns; tool/system appear in the AI SDK ModelMessage
    // history the client echoes back (e.g. tool-result turns after a confirm).
    role: z.enum(['user', 'assistant', 'tool', 'system']),
    // string for plain turns; array of content parts for tool-call/tool-result.
    // A single assistant turn can hold several tool-call parts, so keep headroom.
    content: z.union([z.string().max(20000), z.array(z.any()).max(100)]),
});
export const assistantChatSchema = z.object({
    // Agentic sessions accumulate fast (each tool call adds a call + result
    // message), so this ceiling has to be generous — a multi-step recipe/RFQ
    // build easily passes 50 turns. The natural bound is the model context
    // window / BYOK cost, not this guard. (Client trimming is a future option.)
    messages: z.array(assistantMessageSchema).min(1).max(250),
    approvals: z.record(z.enum(['approve', 'reject'])).optional(),
    // Which saved assistant model to use (id from assistantModels); omit for default.
    modelId: z.string().max(100).optional(),
    // Where the user currently is in the app (route path incl. query), so the
    // assistant can ground "this job" / "this page".
    context: z.object({
        path: z.string().max(300),
    }).optional(),
});
// ----------------------------------------------------------------------------
// Ask Costed conversation history (server-side persistence)
// ----------------------------------------------------------------------------
// One visible transcript bubble (what the user sees).
const conversationDisplayMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    text: z.string().max(20000),
    // Image attachments on user turns (data URLs, client-downscaled ~1280px
    // JPEG). Bounded hard: 3 per message, ~650KB binary each.
    images: z.array(z.string().startsWith('data:image/').max(900_000)).max(3).optional(),
});
// Save (upsert) a conversation. `id` present → update that conversation;
// absent → create a new one. `wire` is the opaque model history (reuses the
// chat message schema, so it tolerates tool/system turns); omitted while a
// confirmation is pending so a dangling tool call is never persisted.
export const saveConversationSchema = z.object({
    id: z.string().uuid().optional(),
    title: z.string().max(200).optional(),
    messages: z.array(conversationDisplayMessageSchema).min(1).max(500),
    wire: z.array(assistantMessageSchema).max(500).optional(),
    modelId: z.string().max(100).optional(),
});
// ============================================================================
// Assistant provider config (bring-your-own-model: Claude/ChatGPT/Gemini/DeepSeek)
// ============================================================================
export const assistantProviderConfigSchema = z.object({
    provider: z.enum(['anthropic', 'openai', 'google', 'deepseek']),
    model: z.string().trim().min(1).max(100),
    apiKey: z.string().trim().min(10).max(400),
});
export const assistantModelAddSchema = assistantProviderConfigSchema.extend({
    label: z.string().trim().max(60).optional(),
});
// ============================================================================
// AI activity (provenance log of AI-performed writes — assistant + MCP)
// ============================================================================
/** Logged by the tool registry after a successful AI write. */
export const aiActivityLogSchema = z.object({
    tool: z.string().trim().min(1).max(80),
    summary: z.string().trim().min(1).max(500),
    refs: z
        .object({
        type: z.enum(['catalogue_item', 'rfq', 'boq_load']),
    })
        .catchall(z.union([z.string().max(120), z.number()]))
        .refine((r) => Object.keys(r).length <= 12 && Object.keys(r).every((k) => k.length <= 40), 'refs must have at most 12 keys with names up to 40 chars')
        .optional(),
});
export const aiActivityReviewSchema = z.object({
    action: z.enum(['reviewed', 'dismissed']),
});
/** Query params for GET /api/help/search (assistant's costed_search_help). */
export const helpSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(200),
    limit: z.coerce.number().int().min(1).max(10).default(5),
});
// ============================================================================
// RFQ quote submission (supplier public response + estimator record-quote)
// ============================================================================
const rfqQuotedItemSchema = z.object({
    rfqItemId: z.string().min(1).max(60),
    quotedPrice: z.number().min(0).max(1_000_000_000),
    supplierReference: z.string().max(120).nullish(),
    notes: z.string().max(2000).nullish(),
    supersededBy: z.string().max(300).nullish(),
    supplierUnit: z.string().max(40).nullish(),
    supplierPackQty: z.number().positive().max(1_000_000).nullish(),
});
/** Body for POST /rfq-response/:slug/:token AND /rfqs/:id/suppliers/:sid/quote. */
export const rfqQuoteSubmitSchema = z.object({
    quoteAmount: z.number().min(0).max(1_000_000_000).optional(),
    quoteNotes: z.string().max(4000).optional(),
    // YYYY-MM-DD only — it's cast to ::date in SQL, so reject anything else here
    // rather than 500 on a bad value (empty string also allowed → treated as null).
    quoteValidUntil: z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal('')]).optional(),
    quotedItems: z.array(rfqQuotedItemSchema).max(1000).default([]),
});
/** Query for GET /api/catalogue/price-intelligence. */
export const priceIntelligenceQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    costCentre: z.string().trim().max(20).optional(),
    staleMonths: z.coerce.number().int().min(1).max(60).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
// ---------- AI report customization (report definitions) ----------
// A report DEFINITION is validated structured config the AI may author — never
// SQL or HTML. Field/groupBy/sort names are validated against the server-side
// report-source field catalogue before render; this schema validates shape only.
export const REPORT_COLUMN_FORMATS = ['text', 'currency', 'integer', 'number', 'percent', 'date', 'boolean'];
export const REPORT_PAGE_SIZES = ['A4', 'Letter', 'Legal'];
export const REPORT_ORIENTATIONS = ['portrait', 'landscape'];
export const reportColumnSchema = z.object({
    field: z.string().trim().min(1).max(64),
    label: z.string().trim().max(120).optional(),
    format: z.enum(REPORT_COLUMN_FORMATS).optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    width: z.string().trim().max(8).optional(),
});
export const reportBrandingSchema = z.object({
    logo: z.boolean().optional(),
    pageSize: z.enum(REPORT_PAGE_SIZES).optional(),
    orientation: z.enum(REPORT_ORIENTATIONS).optional(),
    fontScale: z.number().min(0.8).max(1.3).optional(),
    footerText: z.string().trim().max(200).optional(),
});
export const reportSortSchema = z.object({
    field: z.string().trim().min(1).max(64),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
export const reportDefinitionSchema = z.object({
    version: z.literal(1).default(1),
    base: z.string().trim().min(1).max(64),
    title: z.string().trim().max(160).optional(),
    // Only filter params the chosen base report already accepts; primitive values only.
    filters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    columns: z.array(reportColumnSchema).min(1).max(40),
    groupBy: z.array(z.string().trim().min(1).max(64)).max(2).optional(),
    subtotals: z.boolean().optional(),
    sort: z.array(reportSortSchema).max(3).optional(),
    branding: reportBrandingSchema.optional(),
});
// Tenant-wide branding default (page setup, logo, footer) stored in company metadata.
export const reportBrandingDefaultsSchema = z.object({
    logo: z.boolean().optional(),
    pageSize: z.enum(REPORT_PAGE_SIZES).optional(),
    orientation: z.enum(REPORT_ORIENTATIONS).optional(),
    footerText: z.string().trim().max(200).optional(),
});
// Payload to save a definition (name + the definition itself).
export const saveReportDefinitionSchema = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(['amend', 'new']).optional(),
    origin: z.enum(['ai', 'human']).optional(),
    status: z.enum(['draft', 'published']).optional(),
    definition: reportDefinitionSchema,
});
// ==================== Tier B query blocks (Phase 1) ====================
// Admin-authored (or AI-DRAFTED, Phase 2) read-only SELECT blocks that become
// report sources. The SQL itself is validated by the server-side AST validator
// + run under the dedicated read-only Postgres role — these schemas only police
// the SHAPE of the authoring payload. A block is stored in the tenant table
// `report_query_blocks`; an approved block surfaces as a `qb:<slug>` report source.
export const QUERY_BLOCK_FIELD_TYPES = ['string', 'number', 'currency', 'date', 'percent', 'boolean'];
export const QUERY_BLOCK_PARAM_TYPES = ['string', 'number', 'boolean'];
// A declared OUTPUT column. `name` MUST equal the SELECT alias in the SQL — the
// renderer reads row[name]; a mismatch renders blank (the author sees it in preview).
export const queryBlockColumnSchema = z.object({
    name: z.string().trim().min(1).max(64).regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Column name must be a SQL identifier (letters, digits, underscore).'),
    label: z.string().trim().min(1).max(80),
    type: z.enum(QUERY_BLOCK_FIELD_TYPES).default('string'),
});
// A declared PARAM. Position in the array maps to $1..$n in the SQL.
export const queryBlockParamSchema = z.object({
    name: z.string().trim().min(1).max(64).regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Param name must be a simple identifier.'),
    label: z.string().trim().max(80).optional(),
    type: z.enum(QUERY_BLOCK_PARAM_TYPES).default('string'),
    required: z.boolean().optional(),
});
export const createQueryBlockSchema = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    // The {{schema}}-templated, single-statement read-only SELECT. Hard length cap
    // keeps a pathological payload out of the validator/DB.
    sqlTemplate: z.string().trim().min(1).max(20000),
    columns: z.array(queryBlockColumnSchema).min(1).max(40),
    params: z.array(queryBlockParamSchema).max(16).default([]),
});
export const updateQueryBlockSchema = createQueryBlockSchema.partial();
// Preview an UNSAVED block (or re-run a saved one) during authoring. Param values
// are bound positionally ($1..$n).
export const previewQueryBlockSchema = z.object({
    sqlTemplate: z.string().trim().min(1).max(20000),
    params: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).max(16).default([]),
});
// ── Client portal (homeowner login accounts) ────────────────────────────────
export const portalLoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const portalRefreshSchema = z.object({
    refreshToken: z.string().min(10),
});
export const portalAcceptInviteSchema = z.object({
    token: z.string().min(16),
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
    name: z.string().trim().min(1).max(200).optional(),
});
export const portalForgotPasswordSchema = z.object({
    email: z.string().email(),
});
export const portalResetPasswordSchema = z.object({
    token: z.string().min(16),
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
});
// Staff-side: invite a client to a job's portal.
export const portalInviteSchema = z.object({
    email: z.string().email(),
    name: z.string().trim().min(1).max(200).optional(),
    contactId: z.string().uuid().optional(),
});
// Phase D: client variation response.
export const portalVariationApproveSchema = z.object({
    note: z.string().max(2000).optional(),
});
export const portalVariationDeclineSchema = z.object({
    reason: z.string().trim().min(1, 'A reason is required').max(2000),
});
// ================ Project task dependencies (Gantt) ================
export const dependencyTypeSchema = z.enum([
    'finish_to_start',
    'start_to_start',
    'finish_to_finish',
    'start_to_finish',
]);
export const addDependencySchema = z.object({
    predecessorId: z.string().uuid(),
    dependencyType: dependencyTypeSchema.default('finish_to_start'),
    lagDays: z.number().int().default(0),
});
export const removeDependencyByEdgeSchema = z.object({
    predecessorId: z.string().uuid(),
});
// ================ Gantt scheduling (auto-schedule + working calendar) ================
export const workingCalendarSchema = z.object({
    workdays: z.array(z.number().int().min(0).max(6)).min(1).optional(),
    daysPerWeek: z.number().int().min(1).max(7).optional(),
    holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).max(500).optional(),
});
export const projectSettingsSchema = z.object({
    autoSchedule: z.boolean().optional(),
    calendar: workingCalendarSchema.nullable().optional(),
}).refine(o => o.autoSchedule !== undefined || o.calendar !== undefined, { message: 'Nothing to update' });
export const rescheduleSchema = z.object({
    changedTaskId: z.string().uuid(),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
}).refine(o => o.dueDate >= o.startDate, { message: 'dueDate must be on or after startDate' });
export const importMsProjectSchema = z.object({
    xml: z.string().min(1, 'XML is required').max(25_000_000, 'File is too large'),
});
// ============================================================================
// SUPPLIER / SUBCONTRACTOR ONBOARDING QUESTIONNAIRE
// ============================================================================
// The public questionnaire autosaves progressively, so the submission schema
// validates SHAPE only (everything optional). Required-ness is enforced by the
// server-side completeness engine, which reports human-readable missing items
// — the same list drives the form's progress bar and the follow-up emails.
/** ABN checksum (ATO modulus-89). Accepts spaces/dashes; requires 11 digits. */
export function isValidAbn(abn) {
    const clean = (abn || '').replace(/\D/g, '');
    if (clean.length !== 11)
        return false;
    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    const digits = clean.split('').map(Number);
    digits[0] -= 1;
    const sum = digits.reduce((acc, d, i) => acc + d * weights[i], 0);
    return sum % 89 === 0;
}
/** WHS Regulation 2017 s291 — High-Risk Construction Work classes (SWMS required). */
export const HIGH_RISK_WORK_CLASSES = [
    { key: 'fall_2m', label: 'Work where a person could fall more than 2 metres' },
    { key: 'telecom_tower', label: 'Work on a telecommunication tower' },
    { key: 'demolition', label: 'Demolition of a load-bearing structure' },
    { key: 'asbestos', label: 'Work involving disturbing asbestos' },
    { key: 'structural_alterations', label: 'Structural alterations needing temporary support' },
    { key: 'confined_space', label: 'Work in or near a confined space' },
    { key: 'shaft_trench', label: 'Work in a shaft or trench deeper than 1.5m or a tunnel' },
    { key: 'explosives', label: 'Use of explosives' },
    { key: 'pressurised_gas', label: 'Work on or near pressurised gas mains or piping' },
    { key: 'chemical_fuel_lines', label: 'Work on or near chemical, fuel or refrigerant lines' },
    { key: 'energised_electrical', label: 'Work on or near energised electrical installations' },
    { key: 'contaminated_atmosphere', label: 'Work in an area with a contaminated or flammable atmosphere' },
    { key: 'tilt_up_precast', label: 'Tilt-up or precast concrete work' },
    { key: 'traffic_corridor', label: 'Work on/near a road, railway or other traffic corridor' },
    { key: 'powered_mobile_plant', label: 'Work in an area with movement of powered mobile plant' },
    { key: 'extreme_temperature', label: 'Work in an area with artificial extremes of temperature' },
    { key: 'drowning_risk', label: 'Work in or near water with a risk of drowning' },
    { key: 'diving', label: 'Diving work' },
];
/** Upload requirement keys the questionnaire tracks documents against. */
export const ONBOARDING_DOCUMENT_REQUIREMENTS = [
    'publicLiability',
    'workersComp',
    'personalAccident',
    'professionalIndemnity',
    'contractorLicence',
    'tradeCertificates',
    'whiteCards',
    'swms',
    'sds',
    'other',
];
const onboardingDateSchema = z.union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal('')]);
const onboardingAddressSchema = z.object({
    street: z.string().max(200).optional(),
    suburb: z.string().max(100).optional(),
    state: z.string().max(50).optional(),
    postcode: z.string().max(10).optional(),
});
const onboardingContactSchema = z.object({
    name: z.string().max(120).optional(),
    phone: z.string().max(50).optional(),
    email: z.union([z.string().email().max(200), z.literal('')]).optional(),
});
const onboardingInsuranceSchema = z.object({
    insurer: z.string().max(200).optional(),
    policyNumber: z.string().max(100).optional(),
    expiry: onboardingDateSchema.optional(),
    sumInsured: z.number().min(0).max(1_000_000_000).nullish(),
});
export const supplierOnboardingSubmissionSchema = z.object({
    business: z.object({
        legalName: z.string().max(200).optional(),
        tradingName: z.string().max(200).optional(),
        entityType: z.enum(['company', 'sole_trader', 'partnership', 'trust']).nullish(),
        abn: z.string().max(20).optional(),
        /** Sole traders only: declares they operate without an ABN (payer must
         *  apply no-ABN withholding unless a "Statement by a supplier" is held). */
        noAbn: z.boolean().optional(),
        acn: z.string().max(20).optional(),
        gstRegistered: z.boolean().nullish(),
        address: onboardingAddressSchema.optional(),
        phone: z.string().max(50).optional(),
        email: z.union([z.string().email().max(200), z.literal('')]).optional(),
        website: z.string().max(200).optional(),
        trades: z.string().max(500).optional(),
    }).optional(),
    contacts: z.object({
        primary: onboardingContactSchema.optional(),
        accounts: onboardingContactSchema.optional(),
    }).optional(),
    licences: z.object({
        licenceNumber: z.string().max(100).optional(),
        licenceClass: z.string().max(200).optional(),
        licenceExpiry: onboardingDateSchema.optional(),
        certificates: z.string().max(2000).optional(),
        whiteCards: z.array(z.object({
            name: z.string().max(120),
            cardNumber: z.string().max(60).optional(),
        })).max(100).optional(),
    }).optional(),
    insurances: z.object({
        hasEmployees: z.boolean().nullish(),
        publicLiability: onboardingInsuranceSchema.optional(),
        workersComp: onboardingInsuranceSchema.extend({
            wagesDeclared: z.string().max(100).optional(),
        }).optional(),
        personalAccident: onboardingInsuranceSchema.optional(),
        professionalIndemnity: onboardingInsuranceSchema.extend({
            notRequired: z.boolean().optional(),
        }).optional(),
    }).optional(),
    banking: z.object({
        accountName: z.string().max(200).optional(),
        bankName: z.string().max(100).optional(),
        bsb: z.union([z.string().regex(/^\d{3}-?\d{3}$/), z.literal('')]).optional(),
        accountNumber: z.union([z.string().regex(/^\d{4,10}$/), z.literal('')]).optional(),
        remittanceEmail: z.union([z.string().email().max(200), z.literal('')]).optional(),
    }).optional(),
    whs: z.object({
        policyAcknowledged: z.boolean().optional(),
        highRiskWork: z.array(z.string().max(80)).max(30).optional(),
        usesHazardousSubstances: z.boolean().nullish(),
        inductionConfirmed: z.boolean().optional(),
    }).optional(),
    declarations: z.object({
        whiteCards: z.boolean().optional(),
        inductions: z.boolean().optional(),
        licencesProvided: z.boolean().optional(),
        sdsProvided: z.boolean().optional(),
        insuranceAttached: z.boolean().optional(),
        swmsProvided: z.boolean().optional(),
        marketingConsent: z.boolean().optional(),
    }).optional(),
});
/** Body for PUT /supplier-onboarding/:slug/:token (autosave). */
export const supplierOnboardingSaveSchema = z.object({
    submission: supplierOnboardingSubmissionSchema,
});
/** Body for POST /supplier-onboarding/:slug/:token/submit. */
export const supplierOnboardingSubmitSchema = z.object({
    submission: supplierOnboardingSubmissionSchema,
    signature: z.object({
        name: z.string().min(2).max(120),
        role: z.string().max(120).optional(),
    }),
});
/** Body for POST /suppliers/:code/onboarding/send (staff). */
export const supplierOnboardingSendSchema = z.object({
    email: z.union([z.string().email().max(200), z.literal('')]).optional(),
    contactName: z.string().max(120).optional(),
    kind: z.enum(['subcontractor', 'supplier']).optional().default('subcontractor'),
    expiresInDays: z.number().int().min(1).max(90).optional(),
    message: z.string().max(2000).optional(),
});
/** Body for POST /suppliers/:code/onboarding/return (staff, request changes). */
export const supplierOnboardingReturnSchema = z.object({
    reason: z.string().max(2000).optional(),
    extraItems: z.array(z.string().max(300)).max(50).optional(),
});
/** Body for POST /suppliers/:code/onboarding/verify-item (staff ticked/unticked
 *  "verified against the uploaded document" for one reviewed item). */
export const supplierOnboardingVerifyItemSchema = z.object({
    item: z.enum(['publicLiability', 'workersComp', 'personalAccident', 'professionalIndemnity', 'licence', 'whiteCards']),
    verified: z.boolean(),
});
/** Tenant config stored in company_settings.metadata.supplierOnboarding. */
export const supplierOnboardingConfigSchema = z.object({
    plMinimumSum: z.number().min(0).max(1_000_000_000).optional(),
    reminderDays: z.array(z.number().int().min(1).max(60)).max(10).optional(),
    linkExpiryDays: z.number().int().min(1).max(365).optional(),
    introText: z.string().max(4000).optional(),
    safetyPolicyDocumentId: z.string().uuid().nullish(),
    /** Display-only echo of the chosen document's file name. */
    safetyPolicyDocumentName: z.string().max(300).nullish(),
    marketingConsentEnabled: z.boolean().optional(),
});
